/***
 *
 *   TRANSLATIONS
 *   Resolves the UI locale and exposes `_t` / `setLocale` to the tree.
 *
 *   Resolution order: explicit visitor choice (localStorage) → signed-in
 *   profile language → browser language → English. An unset choice keeps
 *   following the browser, matching how ThemeProvider treats OS colour.
 *
 **********/

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import i18next from 'i18next';
import { getUserCache } from 'utilities/cache';
import languages from '../languages/lang';

export const LOCALES = ['en', 'fr'];
export const LOCALE_STORAGE_KEY = 'valetify-locale';

function supportedLocale(value) {
    if (!value) return null;
    const short = String(value).split('-')[0].toLowerCase();
    return LOCALES.includes(short) ? short : null;
}

function getBrowserLocale() {
    try {
        return supportedLocale(navigator.language);
    } catch (err) {
        return null;
    }
}

function getStoredLocale() {
    try {
        return supportedLocale(localStorage.getItem(LOCALE_STORAGE_KEY));
    } catch (err) {
        return null;
    }
}

function getUserLocale() {
    try {
        const raw = localStorage.getItem('user');
        if (!raw) return null;
        return supportedLocale(JSON.parse(raw)?.language);
    } catch (err) {
        return null;
    }
}

function resolveLocale() {
    return getStoredLocale() || getUserLocale() || getBrowserLocale() || 'en';
}

function persistLocale(locale) {
    try {
        localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch (err) {
        // private browsing / disabled storage — the locale still applies this session
    }
}

function applyLocale(locale) {
    i18next.changeLanguage(locale);
    document.documentElement.lang = locale;
}

const initialLocale = resolveLocale();

i18next.init({
    lng: initialLocale,
    fallbackLng: 'en',
    resources: {
        en: {
            translation: languages.en,
        },
        fr: {
            translation: languages.fr,
        },
    },
});

document.documentElement.lang = initialLocale;

export const _t = i18next.t.bind(i18next);

export function getLocale() {
    return supportedLocale(i18next.language) || resolveLocale();
}

export const TranslateContext = createContext();

export function TranslateProvider(props) {
    const [lang, setLang] = useState(() => {
        const next = resolveLocale();
        applyLocale(next);
        return next;
    });

    useEffect(() => {
        // Cookie hydration can populate `user` after first paint.
        getUserCache();
        const next = resolveLocale();
        applyLocale(next);
        setLang(next);
    }, []);

    const setLocale = useCallback((next) => {
        const locale = supportedLocale(next);
        if (!locale) return;

        persistLocale(locale);
        applyLocale(locale);
        setLang(locale);
    }, []);

    return (
        <TranslateContext.Provider
            value={{
                _t: _t,
                locale: lang,
                setLocale: setLocale,
            }}
            {...props}
        />
    );
}

export const useTranslate = () => {
    return useContext(TranslateContext);
};
